import * as React from 'react'

import { Button } from '../../main/components/button'
import { Icon } from '../../main/components/icon'
import { MapSelect, MapSelectTheme } from '../maps/components/MapSelect'
import { BubblesInput } from './input'
import {
  BubbleSelectOption,
  MARKET_CAP_VALUE,
  averageVolumes,
  colors,
  indexes,
  industries,
  marketCaps,
  sectors,
  sizes,
  xys,
} from './selects'

type OptionName =
  | 'x'
  | 'y'
  | 'size'
  | 'color'
  | 'idx'
  | 'sec'
  | 'ind'
  | 'cap'
  | 'sh_avgvol'
  | 'tickers'
  | 'excludeTickers'

interface SidebarContentProps {
  hasETFs?: boolean
  linkClassName?: string
  extendedFilters?: boolean
  theme?: keyof typeof MapSelectTheme
  selected: Partial<Record<OptionName, string>>
  onChange: (value: Partial<Record<OptionName, string>>) => void
}

export function SidebarContent(props: SidebarContentProps) {
  const { selected, onChange } = props

  const handleSwitchAxis = React.useCallback(() => {
    onChange({ ...selected, x: selected.y, y: selected.x })
  }, [selected, onChange])

  const handleSelect = React.useCallback(
    (item: BubbleSelectOption, name: OptionName) => {
      onChange({ ...selected, [name]: item.value })
    },
    [selected, onChange]
  )

  const handleBlur = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const name = ev.currentTarget.name as OptionName
      const value = ev.currentTarget.value

      if (value !== (selected[name] ?? '')) {
        onChange({ ...selected, [name]: value })
      }
    },
    [selected, onChange]
  )

  const handleKeyDown = React.useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Enter') {
        onChange({
          ...selected,
          [ev.currentTarget.name]: ev.currentTarget.value,
        })
      }
    },
    [selected, onChange]
  )

  const xOptions = React.useMemo(
    () =>
      xys.filter(
        (option) =>
          !(
            (selected.y === 'order' && ['ticker', 'sector'].includes(option.value)) ||
            (option.value === 'order' && ['', 'order', 'sector', 'ticker'].includes(selected.y!))
          )
      ),
    [selected.y]
  )

  const yOptions = React.useMemo(
    () =>
      xys.filter(
        (option) =>
          !(
            (selected.x === 'order' && ['ticker', 'sector'].includes(option.value)) ||
            (option.value === 'order' && ['', 'order', 'sector', 'ticker'].includes(selected.x!))
          )
      ),
    [selected.x]
  )

  const sizeOptions = React.useMemo(
    () =>
      sizes.map((option) => {
        if (props.hasETFs && option.value === MARKET_CAP_VALUE) {
          return { ...option, label: 'Market Capitalization', subtitle: 'ETFs: Assets Under Management' }
        }

        return option
      }),
    [props.hasETFs]
  )

  return (
    <div className="space-y-2">
      <MapSelect
        theme={props.theme}
        name="x"
        label="X Axis"
        value={selected.x}
        items={xOptions}
        onChange={handleSelect}
        comboBox={false}
        listProps={{ className: 'w-52' }}
        nativeSelect="mobile"
        data-testid="x-axis-select"
      />
      <div className="relative">
        <div className="absolute -right-6 -top-5">
          <div className="relative right-1 m-auto h-3 w-2 border-r border-t border-gray-500 border-opacity-50" />
          <Button appearance="square" theme="transparent" className={props.linkClassName} onClick={handleSwitchAxis}>
            <Icon name="switchVertical" />
          </Button>
          <div className="relative right-1 m-auto h-3 w-2 border-b border-r border-gray-500 border-opacity-50" />
        </div>
        <MapSelect
          theme={props.theme}
          name="y"
          label="Y Axis"
          value={selected.y}
          items={yOptions}
          onChange={handleSelect}
          comboBox={false}
          listProps={{ className: 'w-52' }}
          nativeSelect="mobile"
        />
      </div>
      <MapSelect
        theme={props.theme}
        name="size"
        label="Bubble Size"
        value={selected.size}
        items={sizeOptions}
        onChange={handleSelect}
        comboBox={false}
        nativeSelect="mobile"
      />
      <MapSelect
        theme={props.theme}
        name="color"
        label="Bubble Color"
        value={selected.color}
        items={colors}
        onChange={handleSelect}
        comboBox={false}
        nativeSelect="mobile"
      />
      {props.extendedFilters && (
        <>
          <div className="h-px" />
          <MapSelect
            theme={props.theme}
            name="idx"
            label="Index"
            value={selected.idx}
            items={indexes}
            onChange={handleSelect}
            nativeSelect="mobile"
          />
          <MapSelect
            theme={props.theme}
            name="sec"
            label="Sector"
            value={selected.sec}
            items={sectors}
            onChange={handleSelect}
            comboBox={false}
            nativeSelect="mobile"
          />
          <MapSelect
            theme={props.theme}
            name="ind"
            label="Industry"
            value={selected.ind}
            items={industries}
            onChange={handleSelect}
            comboBox={false}
            listProps={{ className: 'w-72' }}
            nativeSelect="mobile"
          />
          <MapSelect
            theme={props.theme}
            name="cap"
            label="Market Cap"
            value={selected.cap}
            items={marketCaps}
            onChange={handleSelect}
            comboBox={false}
            nativeSelect="mobile"
          />

          <MapSelect
            theme={props.theme}
            name="sh_avgvol"
            label="Average Volume"
            value={selected.sh_avgvol}
            items={averageVolumes}
            onChange={handleSelect}
            comboBox={false}
            nativeSelect="mobile"
          />

          <BubblesInput
            name="tickers"
            label="Tickers"
            placeholder="Match any ticker"
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            defaultValue={selected.tickers}
          />
          <BubblesInput
            name="excludeTickers"
            label="Exclude Tickers"
            placeholder="None"
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            defaultValue={selected.excludeTickers}
          />
        </>
      )}
    </div>
  )
}
